import { Component, computed, forwardRef, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { ICONS } from '@core/services/icons/icons.service';
import { isNil } from '@core/utils/shared-utils';

import {
  FieldCustomMap,
  FormBuilderBase
} from '@shared/components/row-edition-form/form-builder/form-builder.model';
import { SelectItem } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';

import { unwrapValue, wrapValue } from '../row-edition-form.utils';

export type CustomMapValueType = 'string' | 'number' | 'boolean';

export interface CustomMapEntry {
  key: string;
  value: string | number | boolean;
  valueType: CustomMapValueType;
}

@Component({
  selector: 'app-custom-map',
  imports: [FormsModule, TranslatePipe, SelectComponent],
  templateUrl: './custom-map.component.html',
  styles: [
    `
      :host {
        display: flex;
        width: 100%;
      }
    `
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomMapComponent),
      multi: true
    }
  ]
})
export class CustomMapComponent implements ControlValueAccessor {
  protected readonly ICONS = ICONS;

  private translate = inject(TranslateService);

  id = input<FormBuilderBase['id']>();
  value = input<Record<string, string | number | boolean> | null>();
  valueTypes = input<FieldCustomMap['valueTypes']>(['string', 'number', 'boolean']);
  wrapperProperty = input<string | undefined>(undefined);

  entries = signal<CustomMapEntry[]>([]);

  protected valueTypeItems = computed<SelectItem[]>(() =>
    this.translatedValueTypes().map((type) => ({ id: type.value, label: type.label }))
  );

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  protected translatedValueTypes = computed(() => {
    // Trigger on language change
    this.currentLang();

    const types = this.valueTypes();
    if (!types) {
      return [];
    }

    return types.map((type) => ({
      value: type,
      label: this.translate.instant(`form.value_types.${type}`)
    }));
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onChange: (_value: any) => void = () => {};
  private onTouched: () => void = () => {};

  addEntry(): void {
    const currentEntries = this.entries();
    const defaultType = this.valueTypes()?.[0] ?? 'string';
    this.entries.set([
      ...currentEntries,
      { key: '', value: this.getDefaultValue(defaultType), valueType: defaultType }
    ]);
    this.notifyChanges();
    this.onTouched();
  }

  removeEntry(index: number): void {
    const currentEntries = this.entries();
    this.entries.set(currentEntries.filter((_, i) => i !== index));
    this.notifyChanges();
    this.onTouched();
  }

  updateKey(index: number, key: string): void {
    const currentEntries = [...this.entries()];
    if (currentEntries[index]) {
      currentEntries[index] = { ...currentEntries[index], key };
      this.entries.set(currentEntries);
      this.notifyChanges();
    }
  }

  updateValue(index: number, value: string | number | boolean): void {
    const currentEntries = [...this.entries()];
    if (currentEntries[index]) {
      const entry = currentEntries[index];
      let parsedValue: string | number | boolean = value;

      if (entry.valueType === 'number' && typeof value === 'string') {
        parsedValue = value === '' ? 0 : parseFloat(value);
        if (isNaN(parsedValue as number)) {
          parsedValue = 0;
        }
      } else if (entry.valueType === 'boolean' && typeof value === 'string') {
        parsedValue = value === 'true';
      }

      currentEntries[index] = { ...entry, value: parsedValue };
      this.entries.set(currentEntries);
      this.notifyChanges();
    }
  }

  changeValueType(index: number, type: CustomMapValueType): void {
    const currentEntries = [...this.entries()];
    if (currentEntries[index]) {
      const entry = currentEntries[index];
      currentEntries[index] = {
        ...entry,
        valueType: type,
        value: this.convertValue(entry.value, type)
      };
      this.entries.set(currentEntries);
      this.notifyChanges();
    }
  }

  onBooleanChange(index: number, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.updateValue(index, checked);
  }

  onBlur(): void {
    this.onTouched();
  }

  private getDefaultValue(type: CustomMapValueType): string | number | boolean {
    switch (type) {
      case 'number':
        return 0;
      case 'boolean':
        return false;
      default:
        return '';
    }
  }

  private convertValue(
    value: string | number | boolean,
    toType: CustomMapValueType
  ): string | number | boolean {
    switch (toType) {
      case 'string':
        return String(value);
      case 'number': {
        const num = parseFloat(String(value));
        return isNaN(num) ? 0 : num;
      }
      case 'boolean':
        return Boolean(value);
    }
  }

  private notifyChanges(): void {
    const entries = this.entries();
    const result: Record<string, string | number | boolean> = {};

    for (const entry of entries) {
      if (entry.key.trim()) {
        result[entry.key] = entry.value;
      }
    }

    this.onChange(Object.keys(result).length > 0 ? result : null);
  }

  writeValue(value: Record<string, string | number | boolean> | null): void {
    const unwrapped = unwrapValue(value, this.wrapperProperty()) as Record<
      string,
      string | number | boolean
    > | null;

    if (!unwrapped || typeof unwrapped !== 'object') {
      this.entries.set([]);
      return;
    }

    const entries: CustomMapEntry[] = Object.entries(unwrapped).map(([key, val]) => ({
      key,
      value: val,
      valueType: this.detectValueType(val)
    }));

    this.entries.set(entries);
  }

  private detectValueType(value: string | number | boolean): CustomMapValueType {
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return 'number';
    return 'string';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.onChange = (value: any) => fn(wrapValue(value, this.wrapperProperty()));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
